import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  ComposedChart,
  Line,
  Legend,
} from 'recharts';
import { Card, Spin, Typography, Row, Col, Statistic, Alert, Select, Space } from 'antd';
import { getPortfolioPerformance } from '../services/api';
import PortfolioPerformanceReport from './PortfolioPerformanceReport';
import { alexforbesBrand } from '../brand/alexforbes';
import { formatDisplayDate, formatShortDate } from '../utils/dates';

const { Text } = Typography;

const MAX_BENCHMARKS = 3;

const RISK_TO_BENCHMARK = {
  conservative: 'usd_conservative',
  balanced: 'usd_balanced',
  growth: 'usd_growth',
  aggressive: 'usd_aggressive',
};

const defaultBenchmarksForRisk = (riskProfile) => {
  const key = String(riskProfile || '').toLowerCase().trim();
  return [RISK_TO_BENCHMARK[key] || 'usd_balanced'];
};

const BENCHMARK_LINE_COLORS = [
  alexforbesBrand.colors.orange,
  '#6B4C9A',
  '#1565C0',
];

const formatDate = (iso) => formatShortDate(iso);

const formatValue = (v) =>
  Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const parseIndex = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const PortfolioPerformanceChart = ({ portfolioId, riskProfile }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [benchmarkIds, setBenchmarkIds] = useState(() =>
    defaultBenchmarksForRisk(riskProfile)
  );
  const [days] = useState(90);
  const [riskDefaultsApplied, setRiskDefaultsApplied] = useState(!riskProfile);

  useEffect(() => {
    if (riskDefaultsApplied || !riskProfile) return;
    setBenchmarkIds(defaultBenchmarksForRisk(riskProfile));
    setRiskDefaultsApplied(true);
  }, [riskProfile, riskDefaultsApplied]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { days };
      if (benchmarkIds.length > 0) {
        params.benchmark = benchmarkIds.join(',');
      }
      const result = await getPortfolioPerformance(portfolioId, params);
      setData(result);
    } catch (e) {
      setError('Unable to load performance data');
    } finally {
      setLoading(false);
    }
  }, [portfolioId, benchmarkIds, days]);

  useEffect(() => {
    load();
  }, [load]);

  const benchmarks = data?.available_benchmarks || [];
  const comparison = data?.comparison;
  const activeBenchmarks = comparison?.benchmarks?.length
    ? comparison.benchmarks
    : comparison?.benchmark
      ? [comparison.benchmark]
      : [];

  const hasComparison =
    Boolean(comparison?.comparison_series?.length) && activeBenchmarks.length > 0;

  const chartData = useMemo(() => {
    if (hasComparison && comparison?.comparison_series?.length) {
      return comparison.comparison_series.map((point) => {
        const row = {
          date: point.date,
          label: formatDate(point.date),
          portfolioIndex: parseIndex(point.portfolio_index),
          portfolioValue: point.portfolio_value ? Number(point.portfolio_value) : null,
        };
        const indices = point.benchmark_indices || {};
        activeBenchmarks.forEach((bench) => {
          const key = `bench_${bench.id}`;
          row[key] =
            indices[bench.id] !== undefined && indices[bench.id] !== ''
              ? parseIndex(indices[bench.id])
              : parseIndex(point.benchmark_index);
        });
        return row;
      });
    }

    return (data?.series || []).map((point) => ({
      date: point.date,
      label: formatDate(point.date),
      value: Number(point.value),
    }));
  }, [data?.series, comparison, hasComparison, activeBenchmarks]);

  const indexedYDomain = useMemo(() => {
    if (!hasComparison) return ['auto', 'auto'];
    const vals = [];
    chartData.forEach((d) => {
      if (d.portfolioIndex != null && Number.isFinite(d.portfolioIndex)) {
        vals.push(d.portfolioIndex);
      }
      activeBenchmarks.forEach((bench) => {
        const v = d[`bench_${bench.id}`];
        if (v != null && Number.isFinite(v)) vals.push(v);
      });
    });
    if (!vals.length) return [90, 110];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = Math.max(2, (max - min) * 0.15);
    return [Math.floor(min - pad), Math.ceil(max + pad)];
  }, [chartData, hasComparison, activeBenchmarks]);

  const change = Number(data?.change_percent || 0);
  const changeColor = change >= 0 ? alexforbesBrand.colors.teal : '#c62828';

  const benchmarkOptions = benchmarks.map((b) => ({
    value: b.id,
    label: b.name,
  }));

  const handleBenchmarkChange = (values) => {
    const next = (values || []).slice(0, MAX_BENCHMARKS);
    setBenchmarkIds(next);
  };

  if (loading && !data) {
    return (
      <Card>
        <Spin />
      </Card>
    );
  }

  if (error && !data) {
    return <Alert type="error" message={error} showIcon />;
  }

  const allWarnings = activeBenchmarks.flatMap((b) => b.warnings || []);

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} align="middle">
        <Col xs={24} md={14}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text strong>Compare to benchmarks (up to {MAX_BENCHMARKS})</Text>
            <Select
              mode="multiple"
              style={{ width: '100%', maxWidth: 520 }}
              value={benchmarkIds}
              onChange={handleBenchmarkChange}
              allowClear
              maxTagCount={MAX_BENCHMARKS}
              placeholder="Portfolio value only (no benchmark)"
              options={benchmarkOptions}
              loading={loading}
            />
            {activeBenchmarks.length > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {activeBenchmarks.map((b) => b.description).filter(Boolean).join(' · ')}
              </Text>
            )}
          </Space>
        </Col>
        {allWarnings.length > 0 && (
          <Col xs={24}>
            <Alert
              type="warning"
              showIcon
              message="Benchmark data notes"
              description={allWarnings.join(' ')}
            />
          </Col>
        )}
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={hasComparison ? 6 : 8}>
          <Card size="small">
            <Statistic
              title={`Start value (${days}d)`}
              value={data?.start_value}
              suffix={data?.currency}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={hasComparison ? 6 : 8}>
          <Card size="small">
            <Statistic
              title="Current value"
              value={data?.end_value}
              suffix={data?.currency}
              precision={2}
            />
          </Card>
        </Col>
        <Col xs={24} sm={hasComparison ? 6 : 8}>
          <Card size="small">
            <Statistic
              title="Portfolio change"
              value={change}
              suffix="%"
              precision={2}
              valueStyle={{ color: changeColor }}
            />
          </Card>
        </Col>
        {hasComparison &&
          activeBenchmarks.map((bench, idx) => {
            const benchChange = Number(bench.change_percent || 0);
            const color = BENCHMARK_LINE_COLORS[idx % BENCHMARK_LINE_COLORS.length];
            return (
              <Col xs={24} sm={6} key={bench.id}>
                <Card size="small">
                  <Statistic
                    title={`${bench.name} (indexed)`}
                    value={benchChange}
                    suffix="%"
                    precision={2}
                    valueStyle={{
                      color: benchChange >= 0 ? color : '#c62828',
                      fontSize: 18,
                    }}
                  />
                </Card>
              </Col>
            );
          })}
      </Row>

      {comparison?.benchmark_error && (
        <Alert type="error" message={comparison.benchmark_error} showIcon style={{ marginBottom: 16 }} />
      )}

      {chartData.length === 0 ? (
        <Alert
          type="info"
          showIcon
          message="No performance history yet"
          description="Add settled trades and instrument prices to build the chart."
        />
      ) : (
        <Card
          size="small"
          title={
            hasComparison
              ? `Indexed performance vs benchmark${activeBenchmarks.length > 1 ? 's' : ''} (${days} days, base = 100)`
              : `Portfolio value (${days} days)`
          }
          extra={loading ? <Spin size="small" /> : null}
        >
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {hasComparison
              ? 'Portfolio and benchmarks rebased to 100 on the same axis so relative performance is comparable. ETF proxies: ACWI, IAGG, BIL, IDV.'
              : `Mark-to-market in ${data?.currency} using historical instrument prices`}
          </Text>
          <ResponsiveContainer width="100%" height={360}>
            {hasComparison ? (
              <ComposedChart data={chartData} margin={{ top: 12, right: 16, left: 12, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 11 }}
                  domain={indexedYDomain}
                  width={58}
                  tickFormatter={(v) => formatValue(v)}
                  label={{
                    value: 'Index (base = 100)',
                    angle: -90,
                    position: 'insideLeft',
                    offset: 4,
                    style: { fontSize: 10, fill: '#666' },
                  }}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (value == null) return ['—', name];
                    if (name === 'Portfolio') return [formatValue(value), 'Portfolio (index)'];
                    return [formatValue(value), `${name} (index)`];
                  }}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.date
                      ? formatDisplayDate(payload[0].payload.date)
                      : ''
                  }
                />
                <Legend verticalAlign="bottom" height={36} />
                <Area
                  type="monotone"
                  dataKey="portfolioIndex"
                  name="Portfolio"
                  stroke={alexforbesBrand.colors.teal}
                  fill={alexforbesBrand.colors.teal}
                  fillOpacity={0.12}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
                {activeBenchmarks.map((bench, idx) => (
                  <Line
                    key={bench.id}
                    type="monotone"
                    dataKey={`bench_${bench.id}`}
                    name={bench.name}
                    stroke={BENCHMARK_LINE_COLORS[idx % BENCHMARK_LINE_COLORS.length]}
                    strokeWidth={2.5}
                    dot={false}
                    connectNulls
                    isAnimationActive={false}
                    legendType="line"
                  />
                ))}
              </ComposedChart>
            ) : (
              <AreaChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="afValueGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={alexforbesBrand.colors.teal} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={alexforbesBrand.colors.teal} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e8" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatValue(v)} width={90} />
                <Tooltip
                  formatter={(value) => [formatValue(value), 'Value']}
                  labelFormatter={(_, payload) =>
                    payload?.[0]?.payload?.date
                      ? formatDisplayDate(payload[0].payload.date)
                      : ''
                  }
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke={alexforbesBrand.colors.teal}
                  fill="url(#afValueGradient)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4, fill: alexforbesBrand.colors.orange }}
                />
              </AreaChart>
            )}
          </ResponsiveContainer>

          {hasComparison && (
            <Space direction="vertical" size={8} style={{ marginTop: 16, width: '100%' }}>
              {activeBenchmarks.map((bench) => {
                const spec = benchmarks.find((b) => b.id === bench.id);
                const weights = (spec?.components || [])
                  .map((c) => `${c.label} ${c.weight}%`)
                  .join(' · ');
                return (
                  <Text key={bench.id} type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    <Text strong style={{ fontSize: 12 }}>
                      {bench.name}:
                    </Text>{' '}
                    {weights || '—'}
                  </Text>
                );
              })}
            </Space>
          )}
        </Card>
      )}
      <PortfolioPerformanceReport
        portfolioId={portfolioId}
        benchmarkId={benchmarkIds.length ? benchmarkIds.join(',') : null}
      />
    </>
  );
};

export default PortfolioPerformanceChart;
